import Icon from '@mdi/react';
import { pdsTv } from '@purinanbm/pds-ui';
import React from 'react';
import { mdiClose } from 'src/assets/icons/mdiIcons';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  label: string | React.ReactElement;
  onClick: React.MouseEventHandler;
  onDelete: React.KeyboardEventHandler;
  'aria-label': string;
  iconJustifyStart?: boolean;
  chipStyle?: 'filled' | 'outlined';
}

function isDeleteKeyboardEvent(event: React.KeyboardEvent) {
  return event.key === 'Backspace' || event.key === 'Delete';
}

const Chip: React.FC<Props> = React.forwardRef<HTMLButtonElement, Props>(
  (
    { className, onClick, onDelete, chipStyle = 'filled', iconJustifyStart = false, ...props },
    ref,
  ) => {
    const chipTv = pdsTv({
      base: [
        'pds-border-pill pds-group/chip pds-inline-flex pds-items-center pds-gap-3.5 pds-rounded-pill pds-border-thick pds-border-surface-layer pds-bg-surface-layer pds-py-3', // base
        'hover:pds-border-surface-layer hover:pds-bg-surface', // hover
        'focus-visible:pds-outline focus-visible:pds-outline-focus', // focus outline
      ],
      variants: {
        chipStyle: {
          filled: '',
          outlined: [
            'pds-border-solid pds-border-surface-line pds-bg-transparent',
            'hover:pds-border-surface-heavy', // hover
          ],
        },
        iconJustifyStart: {
          true: 'pds-pl-3 pds-pr-4',
          false: 'pds-pl-4 pds-pr-3',
        },
      },
      defaultVariants: {
        iconJustifyStart: false,
      },
    });

    const iconTv = pdsTv({
      base: 'pds-rounded-circle pds-bg-surface pds-p-2 group-hover/chip:pds-bg-surface-layer',
      variants: {
        chipStyle: {
          filled: '',
          outlined: 'pds-p-0',
        },
      },
    });

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isDeleteKeyboardEvent(event)) {
        event.preventDefault();
      }
    };

    const handleKeyUp = (event: React.KeyboardEvent) => {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete(event);
      }
    };

    return (
      <button
        type="button"
        ref={ref}
        className={chipTv({ className, chipStyle })}
        onClick={onClick}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {iconJustifyStart && <Icon path={mdiClose} size={1} className={iconTv({ chipStyle })} />}
        <span aria-hidden>{props.label}</span>
        {!iconJustifyStart && <Icon path={mdiClose} size={1} className={iconTv({ chipStyle })} />}
      </button>
    );
  },
);

export default Chip;
